import { useCallback, useContext, useEffect, useState } from "react";
import ListComponent from "../../../components/list/list";
import { IHeadCell } from "../../../components/list/model";
import pubSub, { EVENTS } from "../../../config/pubSub";
import { AppConfig } from "../../../context/context";
import { useDebounce } from "../../../utils/utils";
import { OrganizationsContext } from "../organizations-context";
import { useService } from "../organizations-service";
import { IOrganizationListRowModel } from "../organizations.model";
import { LocalizedDate } from "../../../utils/utils";

const defaultOrganization = {
  count: 500,
  tenantViews: [
    {
      id: "",
      checkbox: false,
      displayName: "",
      email: "",
      status: "",
      created: "-",
      url: "",
    },
  ],
};

const headCells: IHeadCell[] = [
  { id: "id", label: "", display: false },
  { id: "checkbox", label: "", display: true },
  { id: "displayName", label: "cmp_organization_display_name_label", display: true },
  { id: "email", label: "cmp_organization_email_label", display: true },
  { id: "status", label: "cmp_organization_activation_status_label", display: true },
  { id: "created", label: "cmp_organization_date_added_label", display: true },
  { id: "url", label: "cmp_organization_url_title", display: true },
];

type IStatusFilterProps = {
  statusFilter: number;
};

const OrganizationListComponent = (props: IStatusFilterProps) => {
  const [organizations, setOrganizations] = useState<IOrganizationListRowModel>(
    defaultOrganization
  );

  const { config: appConfig } = useContext(AppConfig) || {}; // Destructure and provide default empty object
  const {
    setSelectedOrg,
    page,
    setPage,
    searchTerm,
    rowsPerPage,
    setRowsPerPage,
    setIsOrgListLoading,
    rowsPerPageOptions,
    defaults,
    ListOrgTabs,
    selectedOrg,
    setSelectedCheckedOrgs,
    selectedCheckedOrgs,
  } = useContext(OrganizationsContext);
  const { orgService } = useService();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  //#region 'organization list'

  const formatOrganizationData = (
    rawData: any
  ): IOrganizationListRowModel => {
    return {
      tenantViews: rawData.tenantViews.map((tenant: any) => ({
        id: tenant.id,
        checkbox: false,
        displayName: tenant.displayName,
        email: tenant.email,
        status: tenant.status,
        created:
          "created" in tenant && tenant.created && tenant.created !== "-"
            ? LocalizedDate(appConfig?.language || "", new Date(tenant.created)) // Use optional chaining and default language
            : "-",
        url: tenant.url,
      })),
      count: rawData.count,
    };
  };

  const fetchOrganizations = useCallback(
    async (pageNumber: number, size: number, searchTerm: string) => {
      try {
        setIsOrgListLoading(true);
        const response: IOrganizationListRowModel = await orgService.getAll(
          pageNumber + 1,
          size,
          searchTerm,
          ListOrgTabs[props.statusFilter]
        );
        if (
          (response?.count > 0 && response?.tenantViews?.length > 0) ||
          pageNumber === 0
        ) {
          const formattedData = formatOrganizationData(response);
          setOrganizations(formattedData);
          setCurrentPage(pageNumber);
        } else {
          const catchupResponse: IOrganizationListRowModel =
            await orgService.getAll(
              pageNumber,
              size,
              searchTerm,
              ListOrgTabs[props.statusFilter]
            );
          const formattedData = formatOrganizationData(catchupResponse);
          setOrganizations(formattedData);
          setCurrentPage(pageNumber - 1);
        }
        setIsOrgListLoading(false);
      } catch (error) {
        setOrganizations(defaultOrganization);
        setIsOrgListLoading(false);
      }
    },
    [orgService, props.statusFilter, setIsOrgListLoading, appConfig?.language] // Use optional chaining here too
  );

  const updateFetch = useCallback(() => {
    fetchOrganizations(page, rowsPerPage, debouncedSearchTerm);
  }, [fetchOrganizations, page, rowsPerPage, debouncedSearchTerm]);

  // init updates
  useEffect(() => {
    setOrganizations(defaultOrganization);
    updateFetch();
  }, [updateFetch]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe(EVENTS.ORGANIZATIONS_LIST, () => {
      updateFetch();
    });
    return unsubscribe;
  }, [updateFetch]);

  useEffect(() => {
    // Synchronize selectedRows with selectedCheckedOrgs when selectedCheckedOrgs is empty
    if (selectedCheckedOrgs.length === 0) {
      setSelectedRows([]); // Clear selectedRows when context is cleared
    }
  }, [selectedCheckedOrgs]);

  const handleOrgSelectionChange = (selectedIds: string[]) => {
    setSelectedCheckedOrgs(selectedIds);
    //setSelectedRows(selectedIds); // Update selected rows directly
    setSelectedOrg(selectedIds.length === 1 ? selectedIds[0] : null); // Keep single selection logic
  };

  return (
    <>
      <ListComponent
        rows={organizations ? organizations.tenantViews : []}
        headCells={headCells}
        listConfig={{
          page: currentPage,
          setPage: setPage,
          rowsPerPage: rowsPerPage,
          setRowsPerPage: setRowsPerPage,
          rowsPerPageOptions: rowsPerPageOptions,
          total: organizations ? organizations.count : 0,
        }}
        onSelectionChange={handleOrgSelectionChange}
        selectedRowsProp={selectedRows}
      />
    </>
  );
};

export default OrganizationListComponent;
